/**
 * "Activity today" — the hub's chronological feed of crew posts, status
 * events and weather, read by two surfaces: the hub page itself
 * (Pages/Public/DailyReportShow.vue) and the website builder's
 * **Live from the trails** block. The server half is
 * App\Support\Hub\HubActivity; this is the half that turns its rows
 * into sentences.
 *
 * One definition on purpose. Two copies of `eventText` would drift into
 * two ways of saying the same trail closed, on two pages a visitor can
 * have open side by side.
 */
import { tempValue, fmtSpeed } from '@/lib/units.js';

// ── Status vocabulary ────────────────────────────────────────────────
// Three tones only, so a visitor learns them once: open, caution,
// closed. Everything else maps onto one of them.
export const TONES = {
    open: { ink: '#1f6b3f', bg: '#e6f3ea' },
    caution: { ink: '#8a5a12', bg: '#fdf3dc' },
    closed: { ink: '#a3401f', bg: '#fbeee8' },
    info: { ink: '#2a5a8f', bg: '#e9f0f8' },
    neutral: { ink: '#5d6b63', bg: '#eef1ee' },
};
export const toneStyle = (tone) => {
    const t = TONES[tone] || TONES.neutral;
    return { color: t.ink, background: t.bg };
};

export const trailConditionTone = (s) => ({
    open: 'open', caution: 'caution', closed: 'closed', maintenance: 'caution',
}[s] || 'neutral');

export const liftStateLabel = (s) => ({
    operational: 'Open', wind_hold: 'Wind hold', mechanical_hold: 'Mechanical hold',
    closed: 'Closed', maintenance: 'Maintenance',
}[s] || s || 'Closed');
export const liftStateTone = (s) => ({
    operational: 'open', wind_hold: 'caution', mechanical_hold: 'closed',
    closed: 'closed', maintenance: 'caution',
}[s] || 'neutral');

export function initialsOf(text) {
    const words = String(text).trim().split(/\s+/).filter(Boolean);
    if (!words.length) return '·';
    return words.slice(0, 2).map((w) => w[0]).join('').toUpperCase();
}

export const formatActivityTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
};
const formatHour = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleTimeString(undefined, { hour: 'numeric' });
};

// ── Row text ─────────────────────────────────────────────────────────
const publicTimelineKindLabel = (k) => ({
    trail_status: 'Trail', trail_grooming: 'Grooming', network_status: 'Network',
    area_status: 'Area', lift_status: 'Lift', maintenance: 'Maintenance',
    infrastructure: 'Infrastructure', weather: 'Weather',
}[k] || 'Status');
const publicTrailStateLabel = (s) => ({
    '': 'its default', open: 'Open', caution: 'Caution', closed: 'Closed', maintenance: 'Maintenance',
}[s] ?? (s || 'its default'));
const publicIssueStatusLabel = (s) => ({
    open: 'Open', in_progress: 'In progress', resolved: 'Resolved', pending_review: 'Pending review',
}[s] || s);
const publicGroomingLabel = (s) => ({
    none: 'Not groomed', partial: 'Partially groomed', groomed: 'Groomed',
}[s] || s);

const affected = (n) => (n ? `, ${n} trail${n === 1 ? '' : 's'} affected` : '');

const eventText = (row) => {
    if (row.comment_only) return row.note ? row.note : '';
    let text = '';
    switch (row.kind) {
        case 'trail_status':
            if (row.to_state === '' && row.effective_after) {
                text = `Now ${publicTrailStateLabel(row.effective_after)}${row.effective_source ? ` from ${row.effective_source}` : ''}.`;
            } else {
                text = `Set to ${publicTrailStateLabel(row.to_state)}.`;
            }
            break;
        case 'trail_grooming':
            text = row.from_state && row.from_state === row.to_state
                ? `${publicGroomingLabel(row.to_state)} again.`
                : `${publicGroomingLabel(row.to_state)}.`;
            break;
        case 'network_status':
        case 'area_status':
            text = `Set to ${publicTrailStateLabel(row.to_state)}${affected(row.trails_affected)}.`;
            break;
        case 'lift_status':
            text = `Moved to ${liftStateLabel(row.to_state).toLowerCase()}.`;
            break;
        case 'maintenance':
            if (row.change_kind === 'created') {
                text = row.issue_status === 'pending_review' ? 'Reported, waiting for review.' : 'New maintenance item.';
            } else if (row.change_kind === 'status_changed') {
                text = `Now ${publicIssueStatusLabel(row.to_state).toLowerCase()}.`;
            } else {
                text = 'Updated.';
            }
            break;
        case 'infrastructure':
            text = `New ${row.structure_type ? row.structure_type.toLowerCase() : 'structure'}.`;
            break;
        default:
            text = '';
    }
    return row.note ? `${text} “${row.note}”`.trim() : text;
};

const eventTone = (row) => {
    if (['trail_status', 'network_status', 'area_status'].includes(row.kind)) {
        const s = row.to_state === '' ? row.effective_after : row.to_state;
        return trailConditionTone(s) === 'neutral' ? 'caution' : trailConditionTone(s);
    }
    if (row.kind === 'lift_status') return liftStateTone(row.to_state);
    if (row.kind === 'trail_grooming') return 'info';
    return 'caution';
};

const weatherText = (row, units) => {
    const tempUnit = units === 'imperial' ? 'F' : 'C';
    const parts = [];
    let lead = row.subject || '';
    if (row.is_preview && row.worst_subject) lead += ` with ${row.worst_subject.toLowerCase()} ${row.worst_at_label || ''}`.trimEnd();
    if (!row.is_preview && row.until) lead += ` until ${formatHour(row.until)}`;
    if (lead) parts.push(lead);
    if (row.temp_low_c != null && row.temp_high_c != null) {
        parts.push(row.temp_low_c === row.temp_high_c
            ? `${tempValue(row.temp_low_c, units)}°${tempUnit}`
            : `${tempValue(row.temp_low_c, units)}–${tempValue(row.temp_high_c, units)}°${tempUnit}`);
    }
    if (row.peak_gust_kmh != null) parts.push(`wind to ${fmtSpeed(row.peak_gust_kmh, units)}`);
    return parts.join(', ') + (parts.length ? '.' : '');
};

/**
 * One chronological feed, newest first: crew posts, status events and
 * weather rows, each normalised to the one shape the list draws.
 *
 * @param {{ posts?: Array, timeline?: Array, orgName?: string, units?: string }} input
 */
export function buildActivityFeed({ posts = [], timeline = [], orgName = '', units = 'metric' } = {}) {
    const items = [];
    for (const p of (posts || [])) {
        // No name when the author took theirs off: the post is the
        // organization's then, not ours.
        const who = p.author_name || orgName || 'Trail crew';
        items.push({
            key: `post-${p.id}`, ts: p.posted_at, kind: 'Update', tone: 'update',
            who, initial: initialsOf(who), where: '', text: p.body, post: p,
        });
    }
    (timeline || []).forEach((row, idx) => {
        if (row.kind === 'weather') {
            // A forecast the pipeline has not produced yet is not
            // activity, and would stamp "Updated" with a time nothing
            // happened at.
            if (row.pending) return;
            items.push({
                key: `wx-${row.at}-${idx}`, ts: row.at, kind: 'Weather', tone: 'weather',
                who: row.is_preview ? (row.preview_label || 'Daily forecast') : 'Weather',
                initial: null, where: row.network_name || '', text: weatherText(row, units),
            });
            return;
        }
        items.push({
            key: `ev-${row.kind}-${row.at}-${idx}`, ts: row.at,
            kind: publicTimelineKindLabel(row.kind), tone: eventTone(row),
            who: row.subject || publicTimelineKindLabel(row.kind),
            initial: initialsOf(row.subject || publicTimelineKindLabel(row.kind)),
            where: row.trail_name || '', text: eventText(row),
            pending: row.kind === 'maintenance' && row.issue_status === 'pending_review',
        });
    });
    items.sort((a, b) => String(b.ts || '').localeCompare(String(a.ts || '')));
    return items;
}

const ACTIVITY_CHIPS = {
    update: { background: '#e8efe9', color: '#1f4d33' },
    weather: { background: '#e9f0f8', color: '#2a5a8f' },
};
export const activityChipStyle = (tone) => ACTIVITY_CHIPS[tone] || toneStyle(tone);
